import { useRef, useSyncExternalStore } from 'react';
import {
  useIsMutating,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import {
  campaignPulseQueryKey,
  type CampaignPulse,
} from '@/hooks/useMissionControl';
import { campaignDetailQueryKey } from '@/hooks/useCampaigns';
import { supabase } from '@/lib/supabase';

// Shared across every useArchiveCampaign() instance so pending state can be
// observed globally (F3): two mounts of this hook on different routes
// (e.g. Mission Control and a campaign detail page) each run their own
// useMutation, but both mutations share this key, so useIsMutating can see
// "is ANY archive mutation in flight" regardless of which instance started
// it — closing the race where a route change reopens a control that should
// stay disabled while the write is still pending.
const ARCHIVE_CAMPAIGN_MUTATION_KEY = ['archive-campaign'] as const;

// r5-F4: a module-level mirror of "who is looking at the screen right now",
// refreshed on every render of useArchiveCampaign() below (mirrors the
// ref-assignment pattern already used for organizationIdRef/userIdRef in
// this hook). Unlike those per-hook-instance refs, this is a SINGLE shared
// value, so it is safe to read from onSettled below even when that callback
// fires AFTER the component that started the mutation has unmounted AND
// AFTER AuthContext's queryClient.clear() has already dropped the mutation
// from the QueryClient's own mutation cache. Both Mission Control and the
// campaign detail page call useArchiveCampaign() unconditionally, so an
// identity switch always refreshes this value — via that page's own render
// — before its queries' network fetches actually run (fetches are scheduled
// post-commit, strictly after this render-time assignment).
let currentArchiveIdentity: {
  userId: string | null;
  organizationId: string | null;
} = { userId: null, organizationId: null };

// r5-F4: tracks "is an archive write outstanding" independently of the
// QueryClient's mutation cache, so it survives AuthContext's
// queryClient.clear() (called synchronously on an identity switch, before
// any in-flight archive write settles). useIsMutating alone goes blind the
// instant clear() runs, even though the underlying database write is still
// in flight — this counter does not.
let pendingArchiveWrites = 0;
const pendingArchiveWriteListeners = new Set<() => void>();

function notifyPendingArchiveWriteListeners(): void {
  pendingArchiveWriteListeners.forEach((listener) => listener());
}

function incrementPendingArchiveWrites(): void {
  pendingArchiveWrites += 1;
  notifyPendingArchiveWriteListeners();
}

function decrementPendingArchiveWrites(): void {
  pendingArchiveWrites = Math.max(0, pendingArchiveWrites - 1);
  notifyPendingArchiveWriteListeners();
}

function subscribePendingArchiveWrites(listener: () => void): () => void {
  pendingArchiveWriteListeners.add(listener);
  return () => {
    pendingArchiveWriteListeners.delete(listener);
  };
}

function getPendingArchiveWritesSnapshot(): number {
  return pendingArchiveWrites;
}

export interface ArchiveCampaignVariables {
  campaignId: string;
  archived: boolean;
  // The organization_id of the campaign row the user actually acted on —
  // both Mission Control and the campaign detail page pass their
  // (now org-filtered, see useCampaignPulse/useCampaignDetail)
  // campaign.organization_id here. mutationFn refuses to write unless this
  // matches the CURRENT session organization, so a foreign-org row that
  // briefly entered this organization's cache (RLS visibility can exceed
  // the context org during a membership transition) can never be archived
  // against the session's organization_id (F1).
  campaignOrganizationId: string;
}

interface ArchiveCampaignContext {
  userId: string;
  organizationId: string;
  campaignId: string;
  previousDetail: CampaignPulse | null | undefined;
  previousPulseCampaign: CampaignPulse | undefined;
}

export type ArchiveCampaignMutation = UseMutationResult<
  void,
  Error,
  ArchiveCampaignVariables,
  ArchiveCampaignContext
> & {
  canArchive: boolean;
};

function patchCampaign(
  campaign: CampaignPulse,
  campaignId: string,
  organizationId: string,
  archivedAt: string | null,
): CampaignPulse {
  if (
    campaign.heyreach_campaign_id !== campaignId ||
    campaign.organization_id !== organizationId
  ) {
    return campaign;
  }

  return { ...campaign, archived_at: archivedAt };
}

export function useArchiveCampaign(): ArchiveCampaignMutation {
  const { user, organizationId } = useAuth();
  const userId = user?.id ?? null;
  // Tracks the CURRENT session organization on every render so onError — which
  // fires after the mutation's async work settles, possibly after an identity
  // switch — can tell whether the mutation's originating organization is still
  // the active one before touching the cache. A stale identity comparison here
  // is how an org-A rollback could resurrect org-A rows after the user has
  // moved to org-B (S9a F1 tenant-leak fix).
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  // Tracks the CURRENT authenticated user on every render, alongside
  // organizationIdRef above. os_campaign_stats is USER-SEAT-scoped (RLS),
  // not just organization-scoped, so two members of the SAME organization
  // have independent cache slots — a rollback must only fire when it is
  // still the SAME member who started the mutation, or member A's captured
  // row can be written into member B's actively-read cache (r3-F1 same-org
  // cross-seat cache leak).
  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  // r5-F4: refresh the module-level "who is looking right now" mirror on
  // every render (see currentArchiveIdentity above) — NOT in a useEffect, so
  // it is current before this render's commit schedules any query fetches
  // (including this same page's own campaign pulse/detail reads).
  currentArchiveIdentity = { userId, organizationId };

  const queryClient = useQueryClient();
  const mutation = useMutation<
    void,
    Error,
    ArchiveCampaignVariables,
    ArchiveCampaignContext
  >({
    mutationKey: ARCHIVE_CAMPAIGN_MUTATION_KEY,
    mutationFn: async ({
      campaignId,
      archived,
      campaignOrganizationId,
    }): Promise<void> => {
      if (!organizationId) {
        throw new Error('Campaign archiving requires an organization context.');
      }

      // Guard the write, not just the reads: even with org-filtered pulse/
      // detail queries, a foreign-org row could still reach this mutation
      // (e.g. a stale optimistic patch, or a cache entry populated during a
      // membership-transition RLS window). Refuse to upsert unless the
      // campaign the user acted on is provably the SAME organization as the
      // current session — never trust `organizationId` alone as "the org to
      // write", since that's exactly what would let an org-A session write
      // organization_id=A against a foreign campaign id (F1).
      if (campaignOrganizationId !== organizationId) {
        throw new Error(
          'Campaign archiving refused: campaign organization does not match the session organization.',
        );
      }

      const { error } = await supabase.from('os_campaign_config').upsert(
        {
          organization_id: organizationId,
          heyreach_campaign_id: campaignId,
          archived_at: archived ? new Date().toISOString() : null,
        },
        { onConflict: 'organization_id,heyreach_campaign_id' },
      );

      if (error) throw error;
    },
    onMutate: async ({ campaignId, archived }): Promise<ArchiveCampaignContext> => {
      // r5-F4: increment BEFORE the organization/user guard below so every
      // path through onMutate (including the early throw) is paired with
      // exactly one decrement in onSettled, which always runs regardless of
      // how the mutation terminates.
      incrementPendingArchiveWrites();

      if (!organizationId || !userId) {
        throw new Error('Campaign archiving requires an organization context.');
      }

      const pulseKey = campaignPulseQueryKey(userId, organizationId);
      const detailKey = campaignDetailQueryKey(userId, organizationId, campaignId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: pulseKey }),
        queryClient.cancelQueries({ queryKey: detailKey }),
      ]);

      const previousPulse = queryClient.getQueryData<CampaignPulse[]>(pulseKey);
      const previousPulseCampaign = previousPulse?.find(
        (campaign) =>
          campaign.heyreach_campaign_id === campaignId &&
          campaign.organization_id === organizationId,
      );
      const previousDetail = queryClient.getQueryData<CampaignPulse | null>(detailKey);
      const archivedAt = archived ? new Date().toISOString() : null;

      queryClient.setQueryData<CampaignPulse[]>(pulseKey, (campaigns) =>
        campaigns?.map((campaign) =>
          patchCampaign(campaign, campaignId, organizationId, archivedAt),
        ),
      );
      queryClient.setQueryData<CampaignPulse | null>(detailKey, (campaign) =>
        campaign
          ? patchCampaign(campaign, campaignId, organizationId, archivedAt)
          : campaign,
      );

      return { userId, organizationId, campaignId, previousDetail, previousPulseCampaign };
    },
    onError: (_error, _variables, context): void => {
      // Targeted rollback only: restore the single affected campaign row that
      // onMutate captured — never the whole array snapshot, which could
      // reintroduce every other campaign's stale state (or another
      // organization's/member's rows, if a snapshot were ever taken/restored
      // across an identity switch). Skip the rollback entirely once the
      // session has moved to a different organization OR a different member
      // than the one this mutation started in — writing member-A data back
      // into the cache after an org switch (S9a F1) or a same-org seat switch
      // (r3-F1) is exactly the tenant/seat leak this hook must not reproduce.
      if (
        context &&
        context.userId === userIdRef.current &&
        context.organizationId === organizationIdRef.current
      ) {
        const {
          userId: contextUserId,
          organizationId: contextOrgId,
          campaignId,
          previousPulseCampaign,
        } = context;
        const pulseKey = campaignPulseQueryKey(contextUserId, contextOrgId);
        const detailKey = campaignDetailQueryKey(
          contextUserId,
          contextOrgId,
          campaignId,
        );

        // Existence-preserving contract: `campaigns?.map(...)` returns
        // `undefined` whenever the pulse query has no current cache entry
        // (e.g. evicted by queryClient.clear() during an identity switch
        // that raced ahead of this hook's next render). TanStack's
        // functional setQueryData bails out and does NOT build/recreate the
        // query when the updater returns undefined — so a cleared cache
        // stays cleared instead of resurrecting a stale (possibly
        // cross-organization) snapshot. Defense-in-depth alongside the
        // org-ref guard above, which cannot fire here because it only
        // updates on render and this callback can run after unmount.
        if (previousPulseCampaign) {
          queryClient.setQueryData<CampaignPulse[]>(pulseKey, (campaigns) =>
            campaigns?.map((campaign) =>
              campaign.heyreach_campaign_id ===
                previousPulseCampaign.heyreach_campaign_id &&
              campaign.organization_id === previousPulseCampaign.organization_id
                ? previousPulseCampaign
                : campaign,
            ),
          );
        }

        // Same existence-preserving contract as the pulse rollback above,
        // but expressed explicitly: a VALUE-form `setQueryData(detailKey,
        // context.previousDetail)` would recreate the detail query even
        // after queryClient.clear() removed it, because a plain value
        // (rather than an updater function) is written unconditionally.
        // Bailing (returning undefined) when there is no current entry
        // keeps a cleared/evicted query absent.
        queryClient.setQueryData<CampaignPulse | null>(
          detailKey,
          (current) => (current === undefined ? undefined : context.previousDetail),
        );
      }
      toast.error('Campaign display setting could not be saved.');
    },
    onSuccess: (_data, { archived }): void => {
      toast.success(
        archived
          ? 'Campaign hidden from the default view.'
          : 'Campaign returned to the default view.',
      );
    },
    onSettled: async (_data, _error, { campaignId }): Promise<void> => {
      // r5-F4: decrement first so a thrown invalidateQueries below (e.g. an
      // evicted QueryClient) can never leave the pending counter stuck.
      decrementPendingArchiveWrites();

      // Read from the module-level identity mirror, NOT the render-time
      // `organizationId`/`userId` closure (nor the component-scoped
      // organizationIdRef/userIdRef used in onError above): this callback
      // can fire AFTER an identity switch has unmounted the component that
      // started the mutation, and AFTER AuthContext's queryClient.clear()
      // has already dropped this mutation from the mutation cache. Archiving
      // writes to a SHARED os_campaign_config row, so whoever is now looking
      // at the screen must see the settled result even if they did not
      // start the write — invalidating only the ORIGINATING identity's keys
      // can otherwise leave the CURRENT viewer's cache stale forever, since
      // campaign cache keys are user-seat-scoped and the two identities'
      // keys never overlap (r5-F4).
      const current = currentArchiveIdentity;
      if (!current.userId || !current.organizationId) return;

      const pulseKey = campaignPulseQueryKey(current.userId, current.organizationId);
      const detailKey = campaignDetailQueryKey(
        current.userId,
        current.organizationId,
        campaignId,
      );

      // r7-F2: TanStack Query's Query.fetch() reuses the IN-FLIGHT promise of
      // a query's very first-ever fetch (state.data === undefined) rather
      // than starting a new one — invalidateQueries alone cannot interrupt
      // that fetch, even with its default cancelRefetch behavior, because
      // that early-cancel path itself only triggers when data already exists
      // (query.ts: `if (this.state.data !== undefined && cancelRefetch)`).
      // Concretely: open a campaign detail page (or Mission Control) whose
      // pulse/detail query has not resolved even once yet, start an archive
      // write, and let that pre-write initial fetch keep running — when the
      // write commits, invalidateQueries below would otherwise just mark the
      // query invalidated and wait for the SAME pre-write fetch to resolve,
      // which then clears isInvalidated again and leaves the UI showing the
      // pre-archive state despite a successful write. Explicitly cancelling
      // first (regardless of whether data exists yet) reverts/rejects that
      // in-flight fetch unconditionally, so the invalidation below always
      // starts a genuinely NEW, post-write fetch for the CURRENT viewer.
      await Promise.all([
        queryClient.cancelQueries({ queryKey: pulseKey }),
        queryClient.cancelQueries({ queryKey: detailKey }),
      ]);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: pulseKey }),
        queryClient.invalidateQueries({ queryKey: detailKey }),
      ]);
    },
  });

  return { ...mutation, canArchive: organizationId !== null && userId !== null };
}

/**
 * Whether ANY archive-campaign mutation is currently in flight, across every
 * mounted useArchiveCampaign() instance on the current QueryClient — not
 * just the instance calling this hook (F3). Route-level controls should
 * gate their `disabled` state on this rather than a single instance's local
 * `isPending`, since a route change mounts a fresh useArchiveCampaign()
 * whose own isPending starts false even while an earlier mutation (started
 * on the previous route) is still settling.
 *
 * Also true whenever the module-level pending-write counter is nonzero
 * (r5-F4): useIsMutating alone reads the QueryClient's mutation cache, which
 * AuthContext's queryClient.clear() empties SYNCHRONOUSLY on an identity
 * switch — before the in-flight archive write has actually settled. A
 * freshly-mounted instance of this hook (e.g. the next page, under the new
 * identity) would otherwise report "not pending" even while a shared
 * campaign_config write from the PREVIOUS identity is still in flight.
 */
export function useIsArchiveCampaignPending(): boolean {
  const registryPending = useSyncExternalStore(
    subscribePendingArchiveWrites,
    getPendingArchiveWritesSnapshot,
    getPendingArchiveWritesSnapshot,
  );
  return (
    useIsMutating({ mutationKey: ARCHIVE_CAMPAIGN_MUTATION_KEY }) > 0 ||
    registryPending > 0
  );
}
